import {
  Box,
  Button,
  Flex,
  SelectField,
  Skeleton,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import debounce from 'lodash-es/debounce';
import { useEffect, useMemo, useState } from 'react';

import {
  useAuditLogs,
  useAuditLogsFilters,
  useManager,
} from '@jane/business-admin/data-access';
import { useStoreDetails } from '@jane/business-admin/providers/StoreDetailsProvider';
import type {
  StoreAuditLog,
  StoreAuditLogObject,
} from '@jane/business-admin/types';
import {
  BODY_LINE_HEIGHT,
  LARGE_SKELETON_WIDTH,
  MEDIUM_SKELETON_WIDTH,
} from '@jane/business-admin/util';

const snakeToTitle = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const versionSourceApplicationFormatter = (source_application: string) => {
  return (
    <>
      <br />
      <i>Source: {snakeToTitle(source_application)}</i>
    </>
  );
};

const parentRecordFormatter = (
  parentRecordId: number | null,
  itemType: string | null
) => {
  let parentRecordText = '';
  if (parentRecordId) {
    switch (itemType) {
      case 'StoreSpecialSchedule':
        parentRecordText = `Special ${parentRecordId}`;
    }

    return (
      <>
        <br />
        <i> Parent Record: {parentRecordText}</i>
      </>
    );
  }

  return parentRecordText;
};

const versionActionFormatter = (
  actor_type: string,
  actor_id: string,
  actor_name: string | null,
  internal = false,
  action: string,
  itemType: string,
  itemId: string,
  object: StoreAuditLogObject,
  source_application: string,
  parent_record_id: number | null
) => {
  return (
    <Typography>
      <b>{versionActorFormatter(actor_type, actor_id, actor_name, internal)}</b>
      {versionActionVerbFormatter(action)}&nbsp;
      {versionItemFormatter(itemType, itemId, object, internal)}
      {versionSourceApplicationFormatter(source_application)}
      {parentRecordFormatter(parent_record_id, itemType)}
    </Typography>
  );
};

const versionItemFormatter = (
  itemType: string,
  itemId: string,
  object: StoreAuditLogObject,
  internal = false
) => {
  if (itemType === 'Store') {
    return (
      <>
        {String(itemType)}
        {internal ? (
          <>
            <br />
            <i>
              {itemType} ID: {itemId}
            </i>
          </>
        ) : null}
      </>
    );
  } else if (itemType === 'StoreCrmIntegration') {
    return (
      <>
        CRM Integration - {snakeToTitle(object.crm_provider || '')}
        {internal ? (
          <>
            <br />
            <i>
              {itemType} ID: {itemId}
            </i>
          </>
        ) : null}
      </>
    );
  } else if (itemType === 'Schedule') {
    return (
      <>
        {object.schedule_type?.charAt(0).toUpperCase()}
        {object.schedule_type?.slice(1)}
        {internal ? (
          <>
            <br />
            <i>
              {itemType} ID: {itemId}
            </i>
          </>
        ) : null}
        <br />
      </>
    );
  } else if (itemType.indexOf('Store') === 0) {
    return (
      <>
        {String(itemType.replace('Store', ''))}
        {internal ? (
          <>
            <br />
            <i>
              {itemType} ID: {itemId}
            </i>
          </>
        ) : null}
      </>
    );
  } else if (object.name) {
    return (
      <>
        {object.name}
        <br />
        <i>
          {String(itemType)} ID: {itemId}
        </i>
      </>
    );
  } else {
    return (
      <>
        {String(itemType)} {itemId}{' '}
      </>
    );
  }
};

export const versionActorFormatter = (
  actor_type: string,
  actor_id: string,
  actor_name: string | null,
  internal = false
) => {
  if (actor_type === 'Manager') {
    if (actor_name && actor_name.length > 0) {
      return `${actor_name} `;
    } else {
      return `Manager ${actor_id} `;
    }
  }
  if (internal) {
    if (actor_type === 'AdminUser') {
      if (actor_name && actor_name.length > 0) {
        return `${actor_name} `;
      } else {
        return `AdminUser ${actor_id} `;
      }
    }
  }
  return `Jane Support `;
};

const versionActionVerbFormatter = (action: string) => {
  switch (action) {
    case 'create':
      return 'created';
    case 'update':
      return 'updated';
    case 'destroy':
      return 'deleted';
    default:
      return action;
  }
};

const redactChangeset = (key: string, value: any) => {
  if (key === 'token') {
    return '[REDACTED]';
  }
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const versionChangesetFormatter = (
  changeset: Record<string, any>,
  action: string
) => {
  return Object.entries(changeset || {})
    .filter(([key]) => !['id', 'store_id', 'created_at'].includes(key))
    .map(([key, value]) => (
      <div key={key}>
        <Typography variant="body-bold">{`${key}`}</Typography>
        {action === 'update' && (
          <>
            <Typography>
              updated from <b>{redactChangeset(key, value[0])}</b> to{' '}
              <b>{redactChangeset(key, value[1])}</b>
            </Typography>
          </>
        )}
        {action === 'create' && (
          <Typography>{redactChangeset(key, value[1])}</Typography>
        )}
        {action === 'destroy' && (
          <Typography>{redactChangeset(key, value[0])}</Typography>
        )}
      </div>
    ));
};

export const Audit = () => {
  const { storeId } = useStoreDetails();
  const [filterManagerId] = useState('');
  const [filterItemType, setFilterItemType] = useState('');
  const [filterEntityId, setFilterEntityId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: storeVersions,
    isFetching: isFetchingStoreVersions,
    isPending: isLoadingStoreVersions,
    refetch: refetchAuditLogs,
  } = useAuditLogs({
    storeId,
    filterEntityId: filterEntityId,
    currentPage,
    managerId: filterManagerId,
    itemType: filterItemType,
  });

  const {
    data: storeVersionsFilters,
    isFetching: isFetchingStoreVersionsFilters,
    isPending: isLoadingStoreVersionsFilters,
    refetch: refetchFilters,
  } = useAuditLogsFilters({ storeId });

  const {
    data: manager,
    isFetching: isFetchingManager,
    isPending: isLoadingManager,
  } = useManager();

  const internal = useMemo(() => {
    return manager?.manager.internal;
  }, [manager]);

  const itemTypeToLabel = (itemType: string) => {
    if (itemType === 'Store') {
      return 'Store';
    }
    if (itemType === 'StoreSpecial') {
      return 'Store Special';
    } else if (itemType.indexOf('Store') === 0) {
      return itemType.replace('Store', '');
    } else {
      return itemType;
    }
  };

  const itemTypeSelectOptions = useMemo(() => {
    return storeVersionsFilters?.data?.map((itemType: string) => ({
      label: itemTypeToLabel(itemType),
      value: itemType,
    }));
  }, [storeVersionsFilters]);

  const handleRefresh = () => {
    refetchAuditLogs();
    refetchFilters();
  };

  const debouncedRefresh = useMemo(() => {
    return debounce(handleRefresh, 500);
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [currentPage]);

  useEffect(() => {
    debouncedRefresh();

    return () => {
      debouncedRefresh.cancel();
    };
  }, [filterEntityId, debouncedRefresh]);

  const storeLoading =
    isLoadingStoreVersions || isFetchingStoreVersions || !storeVersions;
  const managerLoading = isLoadingManager || isFetchingManager || !manager;
  const filtersLoading =
    isLoadingStoreVersionsFilters ||
    isFetchingStoreVersionsFilters ||
    !storeVersionsFilters;

  const isLoading = storeLoading || managerLoading || filtersLoading;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" pb={24}>
        <SelectField
          name="filter_itemType"
          placeholder="All models"
          label="Filter by item type"
          labelHidden
          value={filterItemType}
          onChange={(value) => setFilterItemType(value)}
          width="300px"
          options={itemTypeSelectOptions || []}
          disabled={isLoading}
        />
        <TextField
          label="Filter by entity id"
          labelHidden
          width="300px"
          ml={12}
          value={filterEntityId}
          name="filter_entity_id"
          onChange={setFilterEntityId}
          placeholder="Filter by entity id"
        />
        <Button
          onClick={handleRefresh}
          variant="secondary"
          label="Refresh"
          disabled={isLoading}
        />
      </Flex>
      <Flex
        gap={24}
        py={12}
        key="header"
        border="grays-light"
        borderWidth="0 0 1px 0"
      >
        <Flex flexDirection="column" width="15%">
          <Typography variant="body-bold">Time</Typography>
        </Flex>
        <Flex flexDirection="column" width="35%">
          <Typography variant="body-bold">Action</Typography>
        </Flex>
        <Flex flexDirection="column" width="50%">
          <Typography variant="body-bold">Change</Typography>
        </Flex>
      </Flex>
      {isLoading ? (
        <Flex gap={24} py={12}>
          <Flex flexDirection="column" width="15%">
            <Skeleton animate height="fit-content" width="100%">
              <Skeleton.Bone
                width={MEDIUM_SKELETON_WIDTH}
                height={BODY_LINE_HEIGHT}
                mt={8}
              />
            </Skeleton>
          </Flex>
          <Flex flexDirection="column" width="35%">
            <Skeleton animate height="fit-content" width="100%">
              <Skeleton.Bone
                width={LARGE_SKELETON_WIDTH}
                height={BODY_LINE_HEIGHT}
                mt={8}
              />
              <Skeleton.Bone
                width={MEDIUM_SKELETON_WIDTH}
                height={BODY_LINE_HEIGHT}
                mt={8}
              />
            </Skeleton>
          </Flex>
          <Flex flexDirection="column" width="50%">
            <Skeleton animate height="fit-content" width="100%">
              <Skeleton.Bone
                width={MEDIUM_SKELETON_WIDTH}
                height={BODY_LINE_HEIGHT}
                mt={8}
              />
            </Skeleton>
          </Flex>
        </Flex>
      ) : (
        <>
          {storeVersions?.data?.map((auditLog: StoreAuditLog) => (
            <Flex
              key={auditLog.id}
              py={24}
              gap={24}
              border="grays-light"
              borderWidth="0 0 1px 0"
            >
              <Box width="15%">
                <Typography>
                  {new Date(auditLog.created_at * 1000).toLocaleString()}
                </Typography>
              </Box>
              <Box width="35%" pr={12}>
                {versionActionFormatter(
                  auditLog.actor_type,
                  auditLog.whodunnit,
                  auditLog.actor_name,
                  internal,
                  String(auditLog.event),
                  String(auditLog.item_type),
                  String(auditLog.item_id),
                  auditLog.object,
                  auditLog.source_application || '',
                  auditLog.parent_record_id
                )}
              </Box>
              <Box width="50%" pr={12}>
                {versionChangesetFormatter(
                  auditLog.changeset as Record<string, any>,
                  String(auditLog.event)
                )}
              </Box>
            </Flex>
          ))}
        </>
      )}
      <Flex mt={12} justifyContent="center" alignContent="center" gap={64}>
        {storeVersions?.meta?.prev_page && (
          <Button
            onClick={() => setCurrentPage(currentPage - 1)}
            label="← prev page"
          />
        )}
        {storeVersions?.meta?.next_page && (
          <Button
            onClick={() => setCurrentPage(currentPage + 1)}
            label="next page →"
          ></Button>
        )}
      </Flex>
    </Box>
  );
};
